import React from 'react';

/**
 * SlamLoader — Unified platform loading primitive.
 *
 * Uses `var(--slam-gold)` design token — never hardcoded Tailwind color classes.
 * Always renders with `role="status"` for screen-reader compatibility.
 *
 * Variants:
 *   spin  — orbital ring (default)
 *   pulse — single gold dot with opacity pulse
 *   dots  — three-dot staggered bounce
 *
 * @param {Object}  props
 * @param {number}  [props.size=16]              - Pixel dimension of the loader (width and height).
 *                                                 For the `dots` variant this controls dot size and gap.
 * @param {'spin'|'pulse'|'dots'} [props.variant='spin'] - Visual style of the loader.
 * @param {'gold'|'white'|'muted'} [props.color='gold']  - Color token to apply.
 *                                                          `gold`  → `var(--slam-gold, #E8B800)`
 *                                                          `white` → `rgba(255,255,255,0.9)`
 *                                                          `muted` → `rgba(255,255,255,0.35)`
 * @param {string}  [props.label]                - Accessible label for `aria-label`.
 *                                                 Defaults to `'Loading'` when omitted.
 * @returns {React.ReactElement}
 */
export function SlamLoader({ size = 16, variant = 'spin', color = 'gold', label }) {
  const colorMap = {
    gold: 'var(--slam-gold, #E8B800)',
    white: 'rgba(255,255,255,0.9)',
    muted: 'rgba(255,255,255,0.35)',
  };
  const c = colorMap[color] || colorMap.gold;

  const ariaProps = label
    ? { role: 'status', 'aria-label': label }
    : { role: 'status', 'aria-label': 'Loading' };

  if (variant === 'pulse') {
    return (
      <span
        {...ariaProps}
        style={{
          display: 'inline-block',
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: c,
          animation: 'slam-pulse 1.4s ease-in-out infinite',
          flexShrink: 0,
        }}
      />
    );
  }

  if (variant === 'dots') {
    return (
      <span
        {...ariaProps}
        style={{ display: 'inline-flex', gap: size * 0.3, alignItems: 'center' }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: size * 0.35,
              height: size * 0.35,
              borderRadius: '50%',
              backgroundColor: c,
              animation: `slam-dots 1.2s ease-in-out ${i * 0.2}s infinite`,
              display: 'inline-block',
            }}
          />
        ))}
      </span>
    );
  }

  // default: spin
  return (
    <span
      {...ariaProps}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        border: `${Math.max(1.5, size * 0.12)}px solid`,
        borderColor: `${c}30 ${c}30 ${c}30 ${c}`,
        animation: 'slam-spin 0.75s linear infinite',
        flexShrink: 0,
      }}
    />
  );
}

export default SlamLoader;
